import { useCallback, useEffect, useState } from 'react'
import { FocusTrapDialog } from '../../components/FocusTrapDialog'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { UserPicker, type UserPickerValue } from '../../components/UserPicker'
import { ForwardLookingCell } from '../../design-system/list-view/cells/ForwardLookingCell'
import { assignTeamMember, deactivateTeamMember, listCaseTeamMembers, reassignTeamMember } from '../../api/caseTeam'
import { getUiConfig } from '../../api/identity'
import { ApiError } from '../../api/problem'
import { TEAM_ROLES, TEAM_ROLE_LABEL, type CaseTeamMemberView, type TeamRole } from '../../types/caseTeam'

/**
 * The Case Team roster tab (IB-51, docs/design/IB-51-case-team-design.md §6) — lists every
 * `CaseTeamMember` row (active AND inactive, history preserved per AC5) for a Case, with
 * Add/Reassign/Deactivate actions gated behind the `case.team-manage` `/me/ui-config` component
 * key (mirrors `ProductionDetailScreen`'s `film.labour-verify` capability-hide precedent exactly —
 * same honesty caveat: client-side UX only, not a security boundary; the real gate is
 * `CaseTeamService`'s `capabilityResolver.can(actor, "case", "W")`, which still 403s server-side
 * even if this hides the control). `SecurityMatrixSeeder` (module.identity) seeds this key onto
 * `CFA`/`PSC` — the two representative-subset canon profiles that already hold `case:W`-or-broader.
 *
 * <b>IB-328/IB-336:</b> Add/Reassign now use {@link UserPicker} (`GET /api/v1/users/search`) — the
 * raw User-ID text field this screen used to ship (no directory/list endpoint existed) is retired.
 */

const CASE_TEAM_MANAGE_COMPONENT = 'case.team-manage'

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function UserNameCell({ name }: { name: string | null }) {
  return name ? <span>{name}</span> : <ForwardLookingCell reason="user was removed or is not resolvable" />
}

type ModalState = { kind: 'add' } | { kind: 'reassign'; member: CaseTeamMemberView } | null

export interface TeamPanelProps {
  caseId: string
}

export function TeamPanel({ caseId }: TeamPanelProps) {
  const [rows, setRows] = useState<CaseTeamMemberView[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [canManage, setCanManage] = useState(false)
  const [modal, setModal] = useState<ModalState>(null)
  const [busyMemberId, setBusyMemberId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [liveMessage, setLiveMessage] = useState('')

  const announce = useCallback((message: string) => setLiveMessage(message), [])

  const load = useCallback(() => {
    setLoading(true)
    setLoadError(null)
    listCaseTeamMembers(caseId)
      .then((page) => setRows(page.items))
      .catch((err) => setLoadError(errorMessage(err, 'Could not load the case team.')))
      .finally(() => setLoading(false))

    // Capability-hide only (AC12, honesty caveat above) — kept separate from the main load so a
    // ui-config failure never blocks the roster itself from rendering; defaults to hidden.
    getUiConfig()
      .then((config) => setCanManage(config.components.includes(CASE_TEAM_MANAGE_COMPONENT)))
      .catch(() => setCanManage(false))
  }, [caseId])

  useEffect(() => {
    load()
  }, [load])

  const manageReason = canManage
    ? 'You can manage this case team.'
    : 'You do not have permission to manage this case team.'

  async function handleAssign(teamRole: TeamRole, userId: string) {
    setActionError(null)
    try {
      const created = await assignTeamMember(caseId, teamRole, userId)
      setRows((prev) => [...prev, created])
      setModal(null)
      announce(`${TEAM_ROLE_LABEL[teamRole]} assigned.`)
    } catch (err) {
      setActionError(errorMessage(err, 'Could not assign this team member.'))
    }
  }

  async function handleReassign(member: CaseTeamMemberView, newUserId: string) {
    setActionError(null)
    try {
      const created = await reassignTeamMember(caseId, member.id, newUserId)
      setRows((prev) => [
        ...prev.map((r) => (r.id === member.id ? { ...r, active: false } : r)),
        created,
      ])
      setModal(null)
      announce(`${TEAM_ROLE_LABEL[member.teamRole]} reassigned.`)
    } catch (err) {
      setActionError(errorMessage(err, 'Could not reassign this team member.'))
    }
  }

  async function handleDeactivate(member: CaseTeamMemberView) {
    if (busyMemberId) return
    setActionError(null)
    setBusyMemberId(member.id)
    try {
      const updated = await deactivateTeamMember(caseId, member.id)
      setRows((prev) => prev.map((r) => (r.id === member.id ? updated : r)))
      announce(`${TEAM_ROLE_LABEL[member.teamRole]} deactivated.`)
    } catch (err) {
      setActionError(errorMessage(err, 'Could not deactivate this team member.'))
    } finally {
      setBusyMemberId(null)
    }
  }

  const liveRegion = (
    <div aria-live="polite" role="status" className="iw-visually-hidden">
      {liveMessage}
    </div>
  )

  if (loading) {
    return (
      <div>
        {liveRegion}
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading case team…</p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div>
        {liveRegion}
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
              Try again
            </button>
          }
        >
          {loadError}
        </InlineAlert>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      {liveRegion}
      {actionError ? <InlineAlert severity="error">{actionError}</InlineAlert> : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="iw-btn iw-btn--primary"
          aria-disabled={!canManage}
          aria-describedby="team-add-reason"
          onClick={() => canManage && setModal({ kind: 'add' })}
        >
          <Icon name="plus" size={14} /> Add team member
        </button>
        <span id="team-add-reason" className="iw-visually-hidden">
          {manageReason}
        </span>
      </div>

      {rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>
          No team members assigned yet. Add the Case Manager, PSC, or other roles above.
        </p>
      ) : (
        <table className="iw-table" aria-label="Case team">
          <thead>
            <tr>
              <th scope="col">Role</th>
              <th scope="col">User</th>
              <th scope="col">Assigned by</th>
              <th scope="col">Assigned at</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const busy = busyMemberId === row.id
              return (
                <tr key={row.id} style={row.active ? undefined : { color: 'var(--iw-text-subtle)' }}>
                  <td>{TEAM_ROLE_LABEL[row.teamRole]}</td>
                  <td>
                    <UserNameCell name={row.userName} />
                  </td>
                  <td>
                    <UserNameCell name={row.assignedByName} />
                  </td>
                  <td style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                    {formatDate(row.assignedAt)}
                  </td>
                  <td>{row.active ? 'Active' : 'Inactive'}</td>
                  <td style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
                    {row.active ? (
                      <>
                        <button
                          type="button"
                          className="iw-btn iw-btn--sm"
                          aria-disabled={!canManage}
                          aria-describedby={`team-row-reason-${row.id}`}
                          onClick={() => canManage && setModal({ kind: 'reassign', member: row })}
                        >
                          Reassign
                        </button>
                        <button
                          type="button"
                          className="iw-btn iw-btn--sm"
                          aria-disabled={!canManage || busy}
                          aria-describedby={`team-row-reason-${row.id}`}
                          onClick={() => canManage && !busy && void handleDeactivate(row)}
                        >
                          {busy ? 'Deactivating…' : 'Deactivate'}
                        </button>
                        <span id={`team-row-reason-${row.id}`} className="iw-visually-hidden">
                          {manageReason}
                        </span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--iw-text-subtle)', fontSize: 'var(--iw-size-xs)' }}>
                        History — no longer active
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {modal?.kind === 'add' ? (
        <AddTeamMemberModal onClose={() => setModal(null)} onSubmit={handleAssign} />
      ) : null}
      {modal?.kind === 'reassign' ? (
        <ReassignTeamMemberModal
          member={modal.member}
          onClose={() => setModal(null)}
          onSubmit={(newUserId) => handleReassign(modal.member, newUserId)}
        />
      ) : null}
    </div>
  )
}

function AddTeamMemberModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void
  onSubmit: (teamRole: TeamRole, userId: string) => void
}) {
  const [teamRole, setTeamRole] = useState<TeamRole>(TEAM_ROLES[0])
  const [picked, setPicked] = useState<UserPickerValue | null>(null)
  const canSubmit = picked !== null

  return (
    <FocusTrapDialog titleId="add-team-member-title" title="Add team member" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (picked) onSubmit(teamRole, picked.id)
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="add-team-role">
            Role
          </label>
          <select
            id="add-team-role"
            className="iw-input"
            value={teamRole}
            onChange={(e) => setTeamRole(e.target.value as TeamRole)}
          >
            {TEAM_ROLES.map((role) => (
              <option key={role} value={role}>
                {TEAM_ROLE_LABEL[role]}
              </option>
            ))}
          </select>
        </div>
        <UserPicker
          id="add-team-user"
          label="User"
          value={picked}
          onChange={setPicked}
          helpText="Search the tenant's user directory by name or email."
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--iw-space-2)' }}>
          <button type="button" className="iw-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="iw-btn iw-btn--primary" disabled={!canSubmit}>
            Assign
          </button>
        </div>
      </form>
    </FocusTrapDialog>
  )
}

function ReassignTeamMemberModal({
  member,
  onClose,
  onSubmit,
}: {
  member: CaseTeamMemberView
  onClose: () => void
  onSubmit: (newUserId: string) => void
}) {
  const [picked, setPicked] = useState<UserPickerValue | null>(null)
  const canSubmit = picked !== null

  return (
    <FocusTrapDialog
      titleId="reassign-team-member-title"
      title={`Reassign ${TEAM_ROLE_LABEL[member.teamRole]}`}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (picked) onSubmit(picked.id)
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        <div className="iw-field">
          <span className="iw-field-label">Current holder</span>
          <div style={{ color: 'var(--iw-text-muted)' }}>
            <UserNameCell name={member.userName} />
          </div>
        </div>
        <UserPicker
          id="reassign-new-user"
          label="New user"
          value={picked}
          onChange={setPicked}
          helpText="Search the tenant's user directory by name or email."
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--iw-space-2)' }}>
          <button type="button" className="iw-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="iw-btn iw-btn--primary" disabled={!canSubmit}>
            Reassign
          </button>
        </div>
      </form>
    </FocusTrapDialog>
  )
}
